import { useCallback, useEffect } from 'react';

import type { LeaseActionProps } from '../../../models/Types';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';
import LeaseExtension from '../../../models/LeaseExtension';
import { useResendSigningEmailsMutation } from '../../../utils/queries';
import { logError } from '@zg-rentals/log-error';
import { Toast, useToast } from '@zillow/constellation-10';
import { useLeasingUrlParams } from '../../../hooks';

type Props = LeaseActionProps;

function ResendSigningEmailsLeaseExtension({ lease }: Props) {
  const { renewalId } = useLeasingUrlParams();
  const { enqueueToast } = useToast();
  const latestExtension = lease?.latestExtension ? new LeaseExtension(lease.latestExtension) : null;
  const extensionId = renewalId || latestExtension?.id || 0;

  const onEmailSentSuccess = useCallback(() => {
    enqueueToast(<Toast tone="success">Emails for signing successfully resent</Toast>);
  }, [enqueueToast]);

  const onEmailSendError = useCallback(() => {
    enqueueToast(<Toast tone="critical">Emails for signing were not resent, try again later</Toast>);
  }, [enqueueToast]);

  const resendSigningEmailsMutation = useResendSigningEmailsMutation();

  useEffect(() => {
    resendSigningEmailsMutation
      .mutateAsync({
        postData: {
          leaseArtifactId: extensionId,
          leaseArtifactType: LeaseArtifactType.LEASE_EXTENSION_RENEWAL,
        },
      })
      .then(function () {
        onEmailSentSuccess();
        return;
      })
      .catch(function (error) {
        logError({ error, context: 'ResendSigningEmailsLeaseExtension' });
        onEmailSendError();
      });
  }, [extensionId, onEmailSentSuccess, onEmailSendError]);

  return null;
}

export default ResendSigningEmailsLeaseExtension;
